import LogRocket from "logrocket";
import {
  MEMORY_SAMPLING_HEARBEAT,
  useTraceToHoneycomb,
  TraceToHoneycombParams,
} from "tracer";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useRef, useEffect } from "react";
import { getIsInternalUrl } from "components/Dashboard/Tiles/CustomerTiles/customerTilesUtils";
import { MB } from "constants/constants";
// Flight Memory Usage for KK (biggest customer), Total JS heap size:
// (biggest measurement seen within 10s of initial page load)

// Group Tiles http://localhost:3000/kk/dashboard - 230MB
// Process Tiles http://localhost:3000/kk/dashboard/01 - 250MB
// Timeline http://localhost:3000/kk/dashboard/01?view=tl - 270MB
// Hublet http://localhost:3000/kk/dashboard/01/0 - 220MB
// Trends http://localhost:3000/kk/trends?binSize=weeks&ge=1600056000000&lt=1602648000000 - 475MB

/** 800 MB upper limit based on some experimentation */
const MAX_MEMORY_LIMIT = 800 * MB;

/** threshold at which to track / alert */
const MAX_PERCENT_THRESHOLD = 90;

/**
 * - Periodically send memory usage data to honeycomb
 * - If we've neared or exceeded memory usage limits,
 *    track via LogRocket.track, LogRocket.captureMessage, console.warn
 */
export function TrackPerformanceMetricsOnIntervalHooks() {
  const ckey = useCustomerKey();
  const { user } = useAuth0();
  const hasAlarmed = useRef(false);
  const traceToHoneycomb = useTraceToHoneycomb();

  // only check for overages on production flight
  const shouldCheckForOverages = !getIsInternalUrl();

  // start interval on mount, restart whenever we receive user, ckey, or when forceTrace (traceToHoneycomb) changes
  useEffect(() => {
    const perf = window.performance as any;

    // ! window.performance.memory is not yet standardized, works in Chrome only, so don't run if it doesn't exist
    const isBrowserCompatible = perf?.memory && window.requestAnimationFrame;

    // can't trace for incompatible browsers
    if (!isBrowserCompatible) {
      return;
    }
    let timerId;

    const requestId = window.requestAnimationFrame(
      () =>
        (timerId = onFrame({
          user,
          ckey,
          hasAlarmed,
          shouldCheckForOverages,
          traceToHoneycomb,
        }))
    );

    // cancel when dependencies change

    return () => {
      cancelAnimationFrame(requestId);
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [ckey, user, shouldCheckForOverages, traceToHoneycomb]);

  return null;
}

/**
 * send a trace to Honeycomb with memory usage info
 * & alert if we've exceeded the maximum memory limits
 */
function onFrame({
  hasAlarmed,
  user,
  ckey,
  shouldCheckForOverages,
  traceToHoneycomb,
}: {
  hasAlarmed: { current: boolean };
  user: any;
  ckey: string;
  shouldCheckForOverages: boolean;
  traceToHoneycomb: (args: TraceToHoneycombParams) => void;
}): number | void {
  const perf = window.performance as any;

  // https://hackernoon.com/performance-monitoring-for-the-frontend-using-zipkin-bf3aa4a715e5
  // wrap the statements we want to trace in a scoped block and set the current id of the span

  traceToHoneycomb({
    dataToTrace: {
      // span name
      name: "Memory Usage",
      // additional data ("tags")
      customer: String(ckey),
      email: String(user?.email),
      url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
      "memory.totalJSHeapSizeMB": perf.memory.totalJSHeapSize / MB,
      "memory.usedJSHeapSizeMB": perf.memory.usedJSHeapSize / MB,
      "memory.jsHeapSizeLimitMB": perf.memory.jsHeapSizeLimit / MB,
      ...(perf && perf.memory.usedJSHeapSize > MAX_MEMORY_LIMIT
        ? {
            "memory.exceededLimitMB":
              (perf.memory.usedJSHeapSize - MAX_MEMORY_LIMIT) / MB,
          }
        : {}),
    },
    callbackFn: () => {
      // until we've triggered the alarm,
      // check if we have exceeded absolute or relative memory limits
      if (!hasAlarmed.current && shouldCheckForOverages) {
        checkShouldAlarm(perf, hasAlarmed);
      }
    },
  });

  // keep trying to log performance (if it works, that means the app hasn't crashed yet)
  // check on an interval -- shouldn't impact performance since it's using requestAnimationFrame, which waits for priority
  return window.setTimeout(
    () =>
      requestAnimationFrame(() =>
        onFrame({
          hasAlarmed,
          user,
          ckey,
          shouldCheckForOverages,
          traceToHoneycomb,
        })
      ),
    MEMORY_SAMPLING_HEARBEAT
  );
}

/**
 * Check if we’ve exceeded one of two memory limits;
 *
 * one is a hard-coded memory limit of 800MB (const MAX_MEMORY_LIMIT = 800 * MB)
 * the other is basically 90% of the memory available on the device that’s running the app
 *
 * stop checking once we exceed one,
 * since both are really bad and should be avoided, so we should only care about the one that happens first,
 */
function checkShouldAlarm(perf: any, hasAlarmed: { current: boolean }) {
  // Check if we have exceeded absolute memory limit
  if (perf.memory.usedJSHeapSize > MAX_MEMORY_LIMIT) {
    const EXCEEDED_MAXIMUM_MSG = "Exceeded memory maximum limit";
    const overageMb = (perf.memory.usedJSHeapSize - MAX_MEMORY_LIMIT) / MB;

    console.warn(`⚠️⚠️⚠️${EXCEEDED_MAXIMUM_MSG} by ${overageMb} MB`);

    notifyLogRocket(EXCEEDED_MAXIMUM_MSG, {
      exceededByMB: overageMb,
    });

    hasAlarmed.current = true;
  }

  // Check if we have exceeded relative memory limit for client
  if (
    perf.memory.usedJSHeapSize >
    (MAX_PERCENT_THRESHOLD / 100) * perf.memory.jsHeapSizeLimit
  ) {
    const EXCEEDED_THRESHOLD_MSG = `Memory usage exceeded ${MAX_PERCENT_THRESHOLD}% of maximum`;

    console.warn(
      `⚠️⚠️⚠️ ${EXCEEDED_THRESHOLD_MSG}: ${perf.memory.jsHeapSizeLimit}`
    );

    notifyLogRocket(EXCEEDED_THRESHOLD_MSG, {
      extra: {
        jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
        // additional arbitrary data associated with the event
        exceededThresholdByMB:
          perf.memory.usedJSHeapSize -
          (MAX_PERCENT_THRESHOLD / 100) * perf.memory.jsHeapSizeLimit,
      },
    });

    hasAlarmed.current = true;
  }
}

function notifyLogRocket(message: string, extra: any) {
  // logrocket tracking
  LogRocket.track(message);
  // logrocket error message
  LogRocket.captureMessage(message, {
    // additional arbitrary data associated with the event
    extra,
  });
}
